// import React, { ReactNode, useState, createContext, useContext } from 'react';
// import { Notification, NotificationLink, NotificationType } from './types';
// import { Bell, X } from 'lucide-react';
// import Badge from '@material-ui/core/Badge';
// import {
//   IconButton,
//   Paper,
//   Typography,
//   Link as MuiLink,
//   Button,
// } from '@material-ui/core';

// /**
//  * React context to push notifications
//  */
// const NotificationsContext = createContext<
//   (message: string, link?: NotificationLink, type?: NotificationType) => void
// >(() => {
//   throw new Error('useNotifications must be used within NotificationsProvider');
// });

// /**
//  * Provider that holds notifications state and renders bell UI
//  */
// export const NotificationsProvider = ({
//   children,
// }: {
//   children: ReactNode;
// }) => {
//   const [notifications, setNotifications] = useState<Notification[]>([]);
//   const [isOpen, setIsOpen] = useState(false);
//   const push = (
//     message: string,
//     link?: NotificationLink,
//     type: NotificationType = 'info',
//   ) => {
//     // eslint-disable-next-line no-console
//     console.log('push', message, link, type);
//     const id = Date.now().toString();
//     setNotifications(prev => [{ id, message, link, type }, ...prev]);
//   };

//   const dismiss = (id: string) => {
//     setNotifications(prev => {
//       const length = prev.length;
//       if (length - 1 === 0) {
//         setIsOpen(false);
//       }
//       return prev.filter(n => n.id !== id);
//     });
//   };

//   const getColor = (type?: NotificationType) => {
//     switch (type) {
//       case 'success':
//         return 'green';
//       case 'error':
//         return 'red';
//       default:
//         return 'blue';
//     }
//   };
//   const clearNotifications = () => {
//     setNotifications([]);
//     setIsOpen(false);
//   };

//   const AlertBell = () => (
//     <div style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1300 }}>
//       <Badge
//         badgeContent={notifications.length}
//         color="secondary"
//         overlap="circular"
//       >
//         <IconButton
//           aria-label="Notifications"
//           onClick={() => setIsOpen(prev => !prev)}
//           style={{ backgroundColor: 'white' }}
//         >
//           <Bell />
//         </IconButton>
//       </Badge>

//       {isOpen && (
//         <Paper
//           style={{
//             width: 300,
//             padding: 8,
//             maxHeight: '60vh',
//             overflowY: 'auto',
//           }}
//         >
//           <div
//             style={{
//               display: 'flex',
//               justifyContent: 'space-between',
//               alignItems: 'center',
//             }}
//           >
//             <Typography variant="subtitle1">Notifications</Typography>
//             {notifications.length > 0 && (
//               <Button
//                 size="small"
//                 color="secondary"
//                 onClick={clearNotifications}
//                 style={{ textTransform: 'none' }}
//               >
//                 Clear All
//               </Button>
//             )}
//           </div>

//           {notifications.length === 0 ? (
//             <Typography variant="body2" color="textSecondary">
//               No new notifications
//             </Typography>
//           ) : (
//             [...notifications].reverse().map(n => (
//               <div
//                 key={n.id}
//                 style={{
//                   display: 'flex',
//                   justifyContent: 'space-between',
//                   alignItems: 'center',
//                   backgroundColor: `${getColor(n.type)}10`,
//                   borderLeft: `4px solid ${getColor(n.type)}`,
//                   padding: '8px',
//                   marginTop: 8,
//                   borderRadius: 4,
//                 }}
//               >
//                 <div style={{ flex: 1 }}>
//                   <Typography variant="body2">{n.message}</Typography>
//                   {n.link && (
//                     <MuiLink
//                       href={n.link.url}
//                       target="_blank"
//                       rel="noopener"
//                       variant="caption"
//                     >
//                       {n.link.label}
//                     </MuiLink>
//                   )}
//                 </div>
//                 <IconButton size="small" onClick={() => dismiss(n.id)}>
//                   <X size={16} />
//                 </IconButton>
//               </div>
//             ))
//           )}
//         </Paper>
//       )}
//     </div>
//   );

//   return (
//     <NotificationsContext.Provider value={push}>
//       {children}
//       <AlertBell />
//     </NotificationsContext.Provider>
//   );
// };
// /**
//  * Hook to push notifications
//  */
// export function useNotifications() {
//   return useContext(NotificationsContext);
// }
import {
  ReactNode,
  useState,
  createContext,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { Notification, NotificationLink, NotificationType } from './types';
import { Bell, X } from 'lucide-react';
import Badge from '@material-ui/core/Badge';
import {
  IconButton,
  Paper,
  Typography,
  Link as MuiLink,
  Button,
  useTheme,
  makeStyles,
} from '@material-ui/core';

const NotificationsContext = createContext<
  (message: string, link?: NotificationLink, type?: NotificationType) => void
>(() => {
  throw new Error('useNotifications must be used within NotificationsProvider');
});

const getColor = (type?: NotificationType, theme?: any) => {
  if (!theme) return '#1976d2';
  switch (type) {
    case 'success':
      return theme.palette.success.main;
    case 'error':
      return theme.palette.error.main;
    default:
      return theme.palette.primary.main;
  }
};

const useStyles = makeStyles(theme => ({
  toast: {
    width: 300,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  notificationItem: {
    backgroundColor: theme.palette.background.default,
  },
  typeBorder: (props: { type?: NotificationType; theme: any }) => ({
    borderLeft: `4px solid ${getColor(props.type, props.theme)}`,
    backgroundColor: `${getColor(props.type, props.theme)}20`,
  }),
  bellButton: {
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

export const NotificationsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const theme = useTheme();

  // ◀️ new: track that we've loaded from localStorage
  const [initialized, setInitialized] = useState(false);

  // ◀️ your existing state
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentToast, setCurrentToast] = useState<Notification | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ◀️ hydrate from localStorage once on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem('my_app_notifications');
      if (raw) {
        const { notifications: saved, currentToast: savedToast } =
          JSON.parse(raw);
        if (Array.isArray(saved)) setNotifications(saved);
        if (savedToast) setCurrentToast(savedToast);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Could not load saved notifications', e);
    } finally {
      setInitialized(true);
    }
  }, []);

  // ◀️ persist to localStorage whenever notifications or toast change
  useEffect(() => {
    if (!initialized) return; // avoid clobbering loaded state
    const toSave = JSON.stringify({ notifications, currentToast });
    localStorage.setItem('my_app_notifications', toSave);
  }, [notifications, currentToast, initialized]);

  useEffect(() => {
    if (currentToast) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setCurrentToast(null);
      }, 4000);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [currentToast]);

  const push = (
    message: string,
    link?: NotificationLink,
    type: NotificationType = 'info',
  ) => {
    const id = Date.now().toString();
    const newNotification = { id, message, link, type };
    setNotifications(prev => [newNotification, ...prev]);
    setCurrentToast(newNotification);
  };

  const dismiss = (id: string) => {
    setNotifications(prev => {
      const updated = prev.filter(n => n.id !== id);
      if (updated.length === 0) setIsOpen(false);
      return updated;
    });
    if (currentToast?.id === id) setCurrentToast(null);
  };

  const clearNotifications = () => {
    setNotifications([]);
    setIsOpen(false);
    setCurrentToast(null);
  };

  const AlertBell = () => {
    const classes = useStyles({ theme });

    return (
      <div style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1300 }}>
        {/* Toast Notification */}
        {currentToast && (
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              right: 0,
              marginBottom: 8,
              animation: 'slideIn 0.3s ease-out',
            }}
          >
            <Paper
              className={`${classes.toast} ${classes.typeBorder}`}
              style={{ borderLeftColor: getColor(currentToast.type, theme) }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: 8,
                }}
              >
                <div style={{ flex: 1 }}>
                  <Typography variant="body2">
                    {currentToast.message}
                  </Typography>
                  {currentToast.link && (
                    <MuiLink
                      href={currentToast.link.url}
                      target="_blank"
                      rel="noopener"
                      variant="caption"
                      color="secondary"
                    >
                      {currentToast.link.label}
                    </MuiLink>
                  )}
                </div>
                <IconButton size="small" onClick={() => setCurrentToast(null)}>
                  <X size={16} color={theme.palette.text.secondary} />
                </IconButton>
              </div>
            </Paper>
          </div>
        )}

        {/* Bell Icon */}
        <Badge
          badgeContent={notifications.length}
          color="secondary"
          overlap="circular"
        >
          <IconButton
            aria-label="Notifications"
            onClick={() => setIsOpen(prev => !prev)}
            className={classes.bellButton}
          >
            <Bell color={theme.palette.text.primary} />
          </IconButton>
        </Badge>

        {/* Notifications List */}
        {isOpen && (
          <Paper
            className={classes.toast}
            style={{ maxHeight: '60vh', overflowY: 'auto' }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 8,
              }}
            >
              <Typography variant="subtitle1">Notifications</Typography>
              {notifications.length > 0 && (
                <Button
                  size="small"
                  color="secondary"
                  onClick={clearNotifications}
                  style={{ textTransform: 'none' }}
                >
                  Clear All
                </Button>
              )}
            </div>

            {notifications.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: 8 }}
              >
                No new notifications
              </Typography>
            ) : (
              notifications.map(n => {
                const itemClasses = useStyles({ type: n.type, theme }); // eslint-disable-line react-hooks/rules-of-hooks
                return (
                  <div
                    key={n.id}
                    className={`${itemClasses.notificationItem} ${itemClasses.typeBorder}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px',
                      margin: 8,
                      borderRadius: 4,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <Typography variant="body2">{n.message}</Typography>
                      {n.link && (
                        <MuiLink
                          href={n.link.url}
                          target="_blank"
                          rel="noopener"
                          variant="caption"
                          color="secondary"
                        >
                          {n.link.label}
                        </MuiLink>
                      )}
                    </div>
                    <IconButton size="small" onClick={() => dismiss(n.id)}>
                      <X size={16} color={theme.palette.text.secondary} />
                    </IconButton>
                  </div>
                );
              })
            )}
          </Paper>
        )}
      </div>
    );
  };

  return (
    <NotificationsContext.Provider value={push}>
      {children}
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateY(20px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>
      <AlertBell />
    </NotificationsContext.Provider>
  );
};

export function useNotifications() {
  return useContext(NotificationsContext);
}
